import dayjs from './dayjsConfig';
import { FULL_DAY_NAMES, DAY_CODE_TO_INDEX_MAP } from './constants/dates';
import { upperFirst } from 'lodash';
// Note: getVenueTimezone import removed - now handled by smart formatters

/**
 * Calculates the days of the week from a selected date
 * @param {Date|string|number} selectedDate - Date selected as reference point
 * @param {number} startOfWeekIndex - Start day of week (0=Sunday, 1=Monday, etc.)
 * @returns {Array} - Array of objects with day and date for each day of the week
 */

// Better to have a common function for localized date-formatting!
export const formatLocalDate = (value, options) => new Date(value).toLocaleDateString('en-US', options);

export const calculateWeekDays = (selectedDate, startOfWeekIndex = 0) => {
  // Create a Day.js object from the selected date

  let date = dayjs(selectedDate);

  // Validate the date provided
  if (!date.isValid()) {
    console.warn('Fecha inválida proporcionada. Usando fecha actual como respaldo.');
    date = dayjs();
  }

  // configure the start of the week
  date = date.weekday(0).add(startOfWeekIndex, 'day');

  // Generate the days of the week
  const days = [];
  for (let i = 0; i < 7; i++) {
    const currentDay = date.add(i, 'day').startOf('day');
    days.push({
      day: currentDay.format('ddd'), // 'Mon', 'Tue', etc.
      date: currentDay.valueOf(), // Timestamp in milisegundos
    });
  }

  return days;
};

/**
 * Function: getStartOfWeek
 * @description Calculates the timestamp for the start of the week based on a given date and the desired start day of the week.
 * @param {number} timestamp - The timestamp of the reference date.
 * @param {number} startOfWeekIndex - The index of the week start day (0 = Sunday, 1 = Monday, etc.).
 * @returns {number} - The timestamp for the start of the week at 00:00:00.
 */
export const getStartOfWeek = (timestamp, startOfWeekIndex) => {
  // Validate parameters
  if (isNaN(timestamp)) {
    throw new Error('Invalid timestamp provided');
  }

  if (startOfWeekIndex < 0 || startOfWeekIndex > 6) {
    throw new Error('startOfWeekIndex must be between 0 (Sunday) and 6 (Saturday)');
  }

  const date = new Date(timestamp);
  const dayOfWeek = date.getDay(); // 0 (Sunday) to 6 (Saturday)

  // Calculate the start of the week
  const diff = (dayOfWeek - startOfWeekIndex + 7) % 7;
  const startOfWeek = new Date(date.getFullYear(), date.getMonth(), date.getDate() - diff);

  // Set the time at the start of the day
  startOfWeek.setHours(0, 0, 0, 0);

  return startOfWeek.getTime();
};

/**
 * Function: convertTimestampsToUnix
 * @description Converts Firestore Timestamp fields in an object to Unix time (in milliseconds).
 * @param {Object} data - The object containing Firestore Timestamps to be converted.
 * @returns {Object} - A new object with Firestore Timestamps replaced by Unix timestamps in milliseconds.
 */
export const convertTimestampsToUnix = (data) => {
  if (Array.isArray(data)) {
    // If it's an array, map over the elements and call the function recursively
    return data.map(convertTimestampsToUnix);
  } else if (data && typeof data === 'object') {
    // If it's an object, use Object.entries() to iterate over key-value pairs
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => {
        if (value && typeof value === 'object' && 'seconds' in value && 'nanoseconds' in value) {
          // Convert Timestamp to Unix milliseconds
          return [key, value.seconds * 1000];
        }
        // Recursively convert nested objects/arrays
        return [key, convertTimestampsToUnix(value)];
      })
    );
  }
  // If it's not an object or array, return the value as is
  return data;
};

/**
 * Compares two dates to determine if they correspond to the same calendar day
 * regardless of time.
 * @param {number|string|Date} date1 - First date to compare
 * @param {number|string|Date} date2 - Second date to compare
 * @returns {boolean} - true if both dates correspond to the same day
 */
export const isSameDay = (date1, date2) => {
  // To avoid errors, return false if either date is missing
  if (!date1 || !date2) return false;

  // convert dates to Date objects if they are not already
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  // use the year, month, and day of each date to compare
  const formatDate = (date) => {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  };

  return formatDate(d1) === formatDate(d2);
};

/**
 * Checks if a date is in the past (before the current day)
 * @param {number|string|Date} date - Date to check
 * @returns {boolean} - true if the date is in the past
 */
export const isPastDay = (date) => {
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);

  return compareDate < today;
};
/**
 * Function: formatTime
 * @description Formats a time string (HH:mm) to 12-hour format with padded minutes.
 * @param {string} time - The time string in 24-hour format (e.g., "14:30").
 * @returns {string} - The formatted time in 12-hour format (e.g., "2:30").
 */
export const formatTime = (time) => {
  const [hours, minutes] = time.split(':');
  const formattedHours = parseInt(hours, 10) % 12 || 12;
  const formattedMinutes = minutes.padStart(2, '0');
  return `${formattedHours}:${formattedMinutes}`;
};

/**
 * Function: formatShiftTime
 * @description Formats the start and end times of a shift into a readable string in 12-hour format with AM/PM.
 * @param {string|Date} start - The start time of the shift.
 * @param {string|Date} end - The end time of the shift.
 * @param {string} [venueTimeZone] - Optional venue timezone (e.g., 'America/Los_Angeles')
 * @returns {string} - A formatted string representing the shift time (e.g., "9:00 AM - 5:00 PM").
 */
export const formatShiftTime = (start, end, venueTimeZone = null) => {
  const formatTime = (date) => {
    const options = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };

    // If venue timezone is provided, format in that timezone
    if (venueTimeZone) {
      options.timeZone = venueTimeZone;
    }

    return new Intl.DateTimeFormat('en-US', options).format(new Date(date));
  };

  return `${formatTime(start)} - ${formatTime(end)}`;
};

/**
 * Function: calculateTotalHours
 * @description Calculates the total hours from an array of shifts based on their start and end times.
 * @param {Array} shifts - An array of shift objects containing `start` and `end` timestamps.
 * @returns {number} - The total hours worked, rounded to the nearest whole number.
 */
export const calculateTotalHours = (shifts) =>
  Math.round(
    shifts.reduce((total, shift) => {
      if (shift.start && shift.end) {
        return total + (new Date(shift.end) - new Date(shift.start));
      }
      return total;
    }, 0) /
    (1000 * 60 * 60)
  );

/**
 * Function: formatDateToKey
 * @description Converts a `Date` object into a string formatted as "YYYY-MM-DD" for use as a key.
 * @param {Date} dateObj - The `Date` object to format.
 * @returns {string} - The formatted date string (e.g., "2024-12-13").
 */
export function formatDateToKey(dateObj) {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`; // e.g. "2024-12-13"
}

/**
 * Function: formatWeekStrings
 * @description Formats the start and end dates of a week into a readable object with year, month, and day details.
 * @param {Date} startOfWeek - The `Date` object representing the start of the week.
 * @returns {Object} - An object containing `year`, `month`, `start`, and `end` strings representing the week range.
 * @throws {Error} - Throws an error if `startOfWeek` is not a valid `Date` object.
 */
export const formatWeekStrings = (startOfWeek) => {
  // Validate input
  if (!(startOfWeek instanceof Date) || isNaN(startOfWeek)) {
    throw new Error('Invalid startOfWeek parameter. Must be a valid Date object.');
  }

  const MILLISECONDS_IN_A_DAY = 24 * 60 * 60 * 1000;
  const endOfWeek = new Date(startOfWeek.getTime() + 6 * MILLISECONDS_IN_A_DAY); // Calculate end of the week

  // Handle the year, ensuring correct range for weeks crossing year boundaries
  const year = startOfWeek.getFullYear();
  const startDay = startOfWeek.getDate();
  const endDay = endOfWeek.getDate();
  const month = formatLocalDate(startOfWeek, { month: 'short' });

  // Construct the response object
  return {
    year: year.toString(),
    month: upperFirst(month),
    start: startDay.toString(),
    end: endDay.toString(),
  };
};

export function formatDate(dateOrTimestamp, shortOrLong) {
  // Validate input (can be date or timestamp)
  const date = new Date(dateOrTimestamp);
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date provided');
  }

  const abbreviated = shortOrLong === 'short';

  const day = date.getDate();
  const month = formatLocalDate(date, { month: abbreviated ? 'short' : 'long' });
  const dayOfWeek = formatLocalDate(date, { weekday: abbreviated ? 'short' : 'long' });
  const year = abbreviated ? '' : date.getFullYear();

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const dayWithSuffix = `${day}${getOrdinalSuffix(day)}`;
  const formattedDate = `${dayOfWeek}, ${month} ${dayWithSuffix} ${year}`;

  return formattedDate;
}

/**
 * Function: getTimeElapsed
 * @description Calculates the time elapsed since a given start time
 * @param {string|Date} startTime - The reference time to calculate elapsed time from
 * @returns {Object} Object containing hours and minutes elapsed
 *                   - hours: number of complete hours elapsed
 *                   - minutes: remaining minutes (0-59)
 */
export const getTimeElapsed = (startTime) => {
  const now = new Date();
  const start = new Date(startTime);
  const diffMs = now - start;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  return { hours, minutes };
};

/**
 * Function: getRelativeDateType
 * @description Determines if a date is in the past, present (today), or future relative to the current date
 * @param {Date|number|string} date - The date to check
 * @returns {'past'|'today'|'future'} - The relative type of the date
 */
export const getRelativeDateType = (date) => {
  const now = new Date();
  const todayMidnight = new Date(now.setHours(0, 0, 0, 0));
  const shiftDate = new Date(date);
  shiftDate.setHours(0, 0, 0, 0);

  if (shiftDate < todayMidnight) return 'past';
  if (shiftDate.valueOf() === todayMidnight.valueOf()) return 'today';
  return 'future';
};

/**
 * Converts various date formats to a dayjs object
 * @param {Date|Object|string|null} date - The date to convert. Can be:
 *   - Firestore timestamp (object with 'seconds' property)
 *   - dayjs object
 *   - JavaScript Date object
 *   - ISO string
 *   - null
 * @returns {Object|null} A dayjs object, or null if the input is invalid/null
 *
 * @example
 * // Convert Firestore timestamp
 * convertToDayjs({ seconds: 1234567890 })
 *
 * // Convert ISO string
 * convertToDayjs('2024-03-15')
 *
 * // Handle invalid input
 * convertToDayjs(null) // returns null
 */
export const convertToDayjs = (date) => {
  if (!date) return null;

  // Handle Firestore timestamp (which has a 'seconds' property)
  if (date.seconds) {
    return dayjs(date.seconds * 1000);
  }

  // Handle if already a dayjs object
  if (dayjs.isDayjs(date)) {
    return date;
  }

  // Handle other date formats (string, Date object, etc.)
  const converted = dayjs(date);
  return converted.isValid() ? converted : null;
};

export const formatDateForDisplay = (d, format = 'MMM D') => {
  if (!d) return '';
  return dayjs(d).isValid() ? dayjs(d).format(format) : '';
};

export const formatTimeForDisplay = (d) => {
  if (!d) return '';
  return dayjs(d, ['HH:mm', 'h:mm A']).isValid() ? dayjs(d, ['HH:mm', 'h:mm A']).format('h:mm A') : '';
};

export const formatRecurringDays = (days) => {
  if (!days || !days.length) return '';

  const dayIndexes = days.map((day) => (typeof day === 'string' ? DAY_CODE_TO_INDEX_MAP[day] : day));

  return dayIndexes.length === 1
    ? `every ${FULL_DAY_NAMES[dayIndexes[0]]}`
    : `every ${dayIndexes.map((i) => FULL_DAY_NAMES[i]).join(', ')}`;
};

/**
 * Function: getWeekBoundaries
 * @description Gets the start and end boundaries of a week containing the given date, respecting the specified start of week
 * @param {Date|string|number} date - The date to get week boundaries for
 * @param {number} [startOfWeekIndex=0] - The index of the week start day (0 = Sunday, 1 = Monday, etc.)
 * @returns {Object} Object containing weekStart and weekEnd as dayjs objects
 * @example
 * // Get week boundaries starting from Monday
 * getWeekBoundaries(new Date(), 1)
 * // Get week boundaries for default Sunday start
 * getWeekBoundaries(new Date())
 */
export const getWeekBoundaries = (date, startOfWeekIndex = 0) => {
  if (!date) return { weekStart: null, weekEnd: null };

  const dayjsDate = convertToDayjs(date);
  if (!dayjsDate) return { weekStart: null, weekEnd: null };

  // Calculate the start of week by finding how many days to subtract
  const currentDay = dayjsDate.day();
  const daysToSubtract = (currentDay - startOfWeekIndex + 7) % 7;

  const weekStart = dayjsDate.subtract(daysToSubtract, 'day').startOf('day');
  const weekEnd = weekStart.clone().add(6, 'day').endOf('day');

  return { weekStart, weekEnd };
};
